import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

// Types for client translations
export interface ClientTranslation {
  key: string;
  defaultText: string;
  clientText?: string;
  locale: string;
  feature?: string;
  isModified: boolean;
  lastModified?: Date;
  modifiedBy?: string;
}

interface UpdateTranslationParams {
  key: string;
  locale: string;
  value: string;
}

// Hook for fetching client translations
export function useClientTranslations(clientId: string, locale: string, search?: string) {
  return useQuery<ClientTranslation[]>({
    queryKey: ['/api/clients', clientId, 'translations', { locale, search }],
    enabled: !!clientId && !!locale,
  });
}

// Hook for updating single translation
export function useUpdateClientTranslation(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ key, locale, value }: UpdateTranslationParams) => {
      const response = await fetch(`/api/clients/${clientId}/translations/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale, value }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update translation');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'translations'] });
      toast({
        title: "Translation updated",
        description: "The client translation has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update translation.",
        variant: "destructive",
      });
    },
  });
}

// Hook for importing translations
export function useImportClientTranslations(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`/api/clients/${clientId}/translations/import`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to import translations');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'translations'] });
      toast({
        title: "Import successful",
        description: `Imported ${data.imported} translations, updated ${data.updated}.`,
      });
    },
    onError: () => {
      toast({
        title: "Import failed",
        description: "Failed to import translations. Please check your CSV format.",
        variant: "destructive",
      });
    },
  });
}

// Hook for exporting translations
export function useExportClientTranslations(clientId: string) {
  const { toast } = useToast();
  
  return async (locale: string) => {
    try {
      const response = await fetch(`/api/clients/${clientId}/translations/export?locale=${locale}`);
      
      if (!response.ok) {
        throw new Error('Failed to export translations');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations_${locale}_client_${clientId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export successful",
        description: "Translations have been exported to CSV.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export translations.",
        variant: "destructive",
      });
    }
  };
}

// Hook for resetting translations
export function useResetClientTranslations(clientId: string) {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (locale: string) => {
      const response = await fetch(`/api/clients/${clientId}/translations?locale=${locale}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to reset translations');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'translations'] });
      toast({
        title: "Translations reset",
        description: `${data.deleted} client translations have been reset to defaults.`,
      });
    },
    onError: () => {
      toast({
        title: "Reset failed",
        description: "Failed to reset translations.",
        variant: "destructive",
      });
    },
  });
}